import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import { useToast } from '@/hooks/useToast';
import { SectionHeader, Field } from './SectionHeader';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus; disabled: boolean }

// Read identity values with fallback chain: identity sub-object → top-level → ''
function pickName(d: DeviceStatus): string {
  return d.identity?.clientName ?? d.clientName ?? d.client_name ?? '';
}
function pickId(d: DeviceStatus): string {
  return d.identity?.clientID ?? d.client_id ?? '';
}
function pickApiUrl(d: DeviceStatus): string {
  return d.identity?.apiUrl ?? d.apiUrl ?? '';
}

export function IdentitySection({ device, disabled }: Props) {
  const [clientName, setClientName] = useState(pickName(device));
  const [clientID,   setClientID]   = useState(pickId(device));
  const [apiUrl,     setApiUrl]     = useState(pickApiUrl(device));
  const [apiSecret,  setApiSecret]  = useState('');
  const toast = useToast();

  // Re-sync when the device payload changes (firmware echoes back saved values)
  useEffect(() => { setClientName(pickName(device)); }, [device.identity?.clientName, device.clientName, device.client_name]);
  useEffect(() => { setClientID(pickId(device)); },     [device.identity?.clientID, device.client_id]);
  useEffect(() => { setApiUrl(pickApiUrl(device)); },   [device.identity?.apiUrl, device.apiUrl]);

  type IdentityPayload = { clientName: string; clientID: string; apiUrl: string; apiSecret?: string };
  const mut = useDeviceMutation<IdentityPayload>(
    device.client_id, 'identity', { successToast: 'Identity saved' },
  );

  const submit = () => {
    if (!clientName.trim() || !clientID.trim()) { toast.warn('Client name and ID are required'); return; }
    if (!apiUrl.trim()) { toast.warn('Server API URL is required'); return; }
    // OMIT apiSecret entirely if blank, so the firmware's "| apiSecret.c_str()" fallback
    // keeps the existing value. Sending "" would clobber it.
    const payload: IdentityPayload = {
      clientName: clientName.trim(),
      clientID:   clientID.trim(),
      apiUrl:     apiUrl.trim(),
    };
    if (apiSecret) payload.apiSecret = apiSecret;
    mut.mutate(payload);
  };

  return (
    <div className="space-y-5">
      <SectionHeader title="Device identity"
        description="What this device calls itself when it talks to the server. Changing the Client ID will appear as a new device in your fleet." />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client name" htmlFor="id-name">
          <Input id="id-name" value={clientName} disabled={disabled}
            onChange={(e) => setClientName(e.target.value)} placeholder="Solar Cleaner" />
        </Field>
        <Field label="Client ID" htmlFor="id-id">
          <Input id="id-id" value={clientID} disabled={disabled}
            onChange={(e) => setClientID(e.target.value)} placeholder="DEVICE-001" />
        </Field>
      </div>
      <Field label="Server API URL" htmlFor="id-url"
        hint={apiUrl ? undefined : 'Where this device POSTs its status (e.g. https://your-server.com/api/esp/status)'}>
        <Input id="id-url" value={apiUrl} disabled={disabled}
          onChange={(e) => setApiUrl(e.target.value)} placeholder="https://example.com/api/esp/status" />
      </Field>
      <Field label="API secret" htmlFor="id-sec"
        hint="Secrets are never echoed back. Leave blank to keep the current secret; type a new value to replace it.">
        <Input id="id-sec" type="password" value={apiSecret} disabled={disabled}
          onChange={(e) => setApiSecret(e.target.value)} placeholder="Leave blank to keep current" />
      </Field>
      <Button onClick={submit} loading={mut.isPending} disabled={disabled || mut.isPending}>
        Save identity
      </Button>
    </div>
  );
}
